/**
 * Encoder dimensions for a per-shot clip recorder built on a live preview canvas.
 *
 * The preview canvas takes the aspect of the frame slot it fills, so a slot drawn a few
 * pixels thin gives a canvas just as thin: the "IMG_3017" / "IMG_2941" newspaper frames
 * carry a 523 x 2 px photo-3 slot, which comes out as a 1280 x 2-4 px preview. Chromium's
 * H.264 encoder refuses any frame under 16 px on either side (measured on the booth's
 * Electron 24.8.8 / Chrome 112: 1280x14 and 14x16 rejected, 1280x16 and 16x1080 accepted).
 * canvas-record then throws "Unsupported VideoEncoder config" from `start()`, every time,
 * for every session that picks the frame.
 *
 * Pure and import-free so it tests without the renderer.
 */

/** Smallest width and height Chromium's H.264 encoder accepts. */
export const MIN_H264_DIMENSION = 16

/** H.264 needs even dimensions: round each side down to the nearest even number. */
export function evenDimensions(width: number, height: number): { width: number; height: number } {
  return { width: Math.floor(width / 2) * 2, height: Math.floor(height / 2) * 2 }
}

/**
 * Why a canvas of this size cannot be filmed, or null when it can. A non-null answer means
 * creating the recorder is pointless: skip it and let the shot go ahead without a clip.
 */
export function unrecordableReason(width: number, height: number): string | null {
  const even = evenDimensions(width, height)
  if (!(even.width > 0) || !(even.height > 0)) return `no canvas size (${width}x${height})`
  if (even.width < MIN_H264_DIMENSION || even.height < MIN_H264_DIMENSION) {
    return `canvas ${even.width}x${even.height} is below the ${MIN_H264_DIMENSION} px H.264 minimum (the frame slot is too thin to film)`
  }
  return null
}
